import React from "react"
import { useEffect, useState } from "react"
import { Spinner } from "@trimbleinc/modus-react-bootstrap"
import { useAppContext } from "../contexts/AppContext"
import QuadriRestApi, { TaskType } from "../apis/quadriRestApi"
import { ModusTreeView, ModusTreeViewItem } from "@trimble-oss/modus-react-components"
import { TaskPropertyPanel } from "./TaskPropertyPanel"
import TaskTreeItemComponent from "./TaskTreeItemComponent"
import { TaskTypeDefinitionsCache } from "../apis/TaskTypeDefinitionsCache"

const TaskTreeComponent = () => {
  const { projectID, accessToken, projectLocation, API } = useAppContext()
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<boolean>(false)
  const [rootNode, setRootNode] = useState<TaskType>()
  const [selectedNode, setSelectedNode] = useState<TaskType>()
  const [taskIndex, setTaskIndex] = useState<Map<string, TaskType>>(new Map())
  const numLevelsToFetch = 2

  useEffect(() => {
    const fetchTaskModels = async () => {
      try {
        const restApi = new QuadriRestApi()
        await restApi.initialize(accessToken, projectLocation)

        await TaskTypeDefinitionsCache.getInstance().init(accessToken, projectLocation)

        const taskTree = await restApi.getTaskTree(projectID, numLevelsToFetch)
        setRootNode(taskTree)
        indexTaskType(taskTree)
      } catch (error) {
        console.error("Error fetching task tree:", error)
      } finally {
        setLoading(false)
      }
    }
    fetchTaskModels()
  }, [accessToken, projectID, projectLocation])

  const indexTaskType = (taskType: TaskType) => {
    setTaskIndex((prevIndex) => {
      const newIndex = new Map(prevIndex)
      const indexTasks = (task: TaskType) => {
        if (!task || !task.header) return
        newIndex.set(task.header.taskId, task)
        task.children?.forEach(indexTasks)
      }
      indexTasks(taskType)
      return newIndex
    })
  }

  const handleNodeExpandToggle = async (event: any, nodeId: string) => {
    event.stopPropagation()
    if (!expanded) setExpanded(true)
  }

  const renderTaskTree = (taskTree: TaskType | undefined) => {
    if (!taskTree || !taskTree.header) {
      return <div>Error: Task tree data is unavailable.</div>
    }
    return (
      <ModusTreeView className="task-tree" borderless={true} size="condensed">
        {renderTaskType(taskTree.header.taskId)}
      </ModusTreeView>
    )
  }

  const renderTaskType = (taskTypeId: string) => {
    const taskType = taskIndex.get(taskTypeId)

    if (!taskType) return null

    return (
      <ModusTreeViewItem
        key={taskType!.header.taskId}
        nodeId={taskType!.header.taskId}
        label={taskType!.header.name}
        onItemExpandToggle={(event) => handleNodeExpandToggle(event, taskType!.header.taskId)}
      >
        {taskType!.details?.type?.iconSvg}
        {taskType?.children?.map((child) => (
          <TaskTreeItemComponent
            key={child.header.taskId}
            taskType={child}
            taskTypeId={child.header.taskId}
            parentExpanded={expanded}
            setSelectedNode={setSelectedNode}
          />
        ))}
      </ModusTreeViewItem>
    )
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="px-3">
          <div className="text-center">
            <Spinner animation="border" variant="primary" role="status" />
            <div className="h3 text-primary mt-3">Loading...</div>
          </div>
        </div>
      )
    }
    return renderTaskTree(rootNode!)
  }

  return (
    <div>
      <div className="tasktree-view-content">{renderContent()}</div>
      <div>
        <TaskPropertyPanel data={selectedNode} />
      </div>
    </div>
  )
}

export default TaskTreeComponent
